import React, { useState } from "react";
import styles from "./AddProductModal.module.css";
import Select from "react-select";
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  CircularProgress,
} from "@mui/material";

const AddProductModal = ({ onClose, refetchAllProducts }) => {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [isLoaded, setIsLoaded] = React.useState(false);

  React.useEffect(() => {
    async function fetchCategoryData() {
      setIsLoaded(false);
      try {
        fetch(`${process.env.REACT_APP_SERVER_URL}/get_all_categories`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "GET",
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else {
              console.log("some error");
              setIsLoaded(true);
              return response.json();
            }
          })
          .then((data) => {
            setCategories(data.data);
            setIsLoaded(true);
          })
          .catch((error) => console.log(error));
      } catch (error) {
        console.log(error);
        setIsLoaded(true);
      }
    }
    async function fetchSubCategoryData() {
      try {
        fetch(`${process.env.REACT_APP_SERVER_URL}/get_all_subcategories`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "GET",
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else {
              setIsLoaded(true);
              console.log("some error");
              return response.json();
            }
          })
          .then((data) => {
            setSubCategories(data.data);
            setIsLoaded(true);
          })
          .catch((error) => console.log(error));
      } catch (error) {
        console.log(error);
        setIsLoaded(true);
      }
    }
    fetchCategoryData();
    fetchSubCategoryData();
  }, []);

  // UI state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  const [openCategoryDrawer, setOpenCategoryDrawer] = useState(false);
  const [openSubCategoryDrawer, setOpenSubCategoryDrawer] = useState(false);

  // Drawer inputs
  const [newCategory, setNewCategory] = useState("");
  const [newSubCategory, setNewSubCategory] = useState("");
  const [newSubCategoryCat, setNewSubCategoryCat] = useState(null);
  const [form, setForm] = useState({
    app_name: "",
    img_url: "",
    description: "",
    sub_category_id: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const productData = {
      app_name: form.app_name,
      img_url: form.img_url,
      pricing: {
        india: [],
        global: [],
      },
      description: form.description,
      sub_category_id: form.sub_category_id,
      parent_id: null,
      children_ids: [],
      app_details: [],
      similar_apps: [],
      features: [],
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/add-product`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(productData),
        }
      );

      if (res.status === 200) {
        alert("Product added successfully");
        onClose();
        refetchAllProducts();
      } else {
        const err = await res.json();
        alert("Error: " + (err.message || err.msg || "Failed to add product"));
      }
    } catch (error) {
      console.error("Fetch error:", error);
      alert("Network error while adding product.");
    }
  };

  const categoryOptions = categories?.map((c) => ({
    value: c.id,
    label: c.name,
  }));
  const subCategoryOptions = subCategories
    ?.filter((sc) => selectedCategory && sc.category_id === selectedCategory.id)
    ?.map((sc) => ({ value: sc.id, label: sc.name }));

  const addCategory = async () => {
    const name = newCategory.trim();
    if (!name) return;
    const res = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/add_category`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: name }),
      }
    );

    if (res.ok) {
      alert("Category added successfully");
      onClose();
    } else {
      const err = await res.json();
      alert("Error: " + (err.message || "Failed to add product"));
    }
    setNewCategory("");
    setOpenCategoryDrawer(false);
  };

  const addSubCategory = async () => {
    const name = newSubCategory.trim();
    if (!name || !newSubCategoryCat) return;
    const res = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/add_subcategory`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: name,
          category_id: selectedCategory,
        }),
      }
    );

    if (res.ok) {
      alert("Category added successfully");
      onClose();
    } else {
      const err = await res.json();
      alert("Error: " + (err.message || "Failed to add product"));
    }
    setNewSubCategory("");
    setNewSubCategoryCat(null);
    setOpenSubCategoryDrawer(false);
  };

  return (
    <>
      {!isLoaded && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}
      {isLoaded && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <h2>Add Product</h2>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="app_name"
                placeholder="App Name"
                value={form.app_name}
                onChange={handleChange}
                required
              />

              <input
                type="text"
                name="img_url"
                placeholder="Image URL"
                value={form.img_url}
                onChange={handleChange}
                required
              />

              <textarea
                name="description"
                placeholder="Enter product description"
                value={form.description}
                onChange={handleChange}
                rows={4}
                required
              />

              <div style={{ marginBottom: "2%" }}>
                <Select
                  options={categoryOptions}
                  value={
                    selectedCategory
                      ? {
                          value: selectedCategory.id,
                          label: selectedCategory.name,
                        }
                      : null
                  }
                  onChange={(option) => {
                    const cat =
                      categories.find((c) => c.id === option.value) || null;
                    setSelectedCategory(cat);
                    setSelectedSubCategory(null); // reset subcat
                  }}
                  placeholder="Select Category"
                />
                <button
                  style={{ marginTop: "2%" }}
                  type="button"
                  onClick={() => setOpenCategoryDrawer(true)}
                >
                  + Add
                </button>
              </div>

              {/* SubCategory Select + Add */}
              <div style={{ marginBottom: "2%" }}>
                <Select
                  options={subCategoryOptions}
                  value={
                    selectedSubCategory
                      ? {
                          value: selectedSubCategory.id,
                          label: selectedSubCategory.name,
                        }
                      : null
                  }
                  onChange={(option) => {
                    const sub =
                      subCategories.find((s) => s.id === option.value) || null;
                    setSelectedSubCategory(sub);
                  }}
                  placeholder="Select Sub Category"
                  className="flex-1"
                  isDisabled={!selectedCategory}
                  noOptionsMessage={() =>
                    selectedCategory
                      ? "No subcategories"
                      : "Select a category first"
                  }
                />
                <button
                  type="button"
                  style={{ marginTop: "2%" }}
                  onClick={() => {
                    // Default the drawer's category to the currently selected one
                    if (selectedCategory)
                      setNewSubCategoryCat(selectedCategory);
                    setOpenSubCategoryDrawer(true);
                  }}
                >
                  + Add
                </button>
              </div>

              <div className={styles.buttonRow}>
                <button type="submit">Submit</button>
                <button type="button" onClick={onClose}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
          <Drawer
            anchor="right"
            open={openCategoryDrawer}
            onClose={() => setOpenCategoryDrawer(false)}
            sx={{ width: "50%" }}
          >
            <Box sx={{ width: 340, p: 2 }} role="presentation">
              <Typography variant="h6" sx={{ mb: 1 }}>
                Add Category
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TextField
                label="Category Name"
                fullWidth
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                autoFocus
              />
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Button variant="contained" onClick={addCategory}>
                  Save
                </Button>
                <Button onClick={() => setOpenCategoryDrawer(false)}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Drawer>

          {/* Drawer: Add Sub Category */}
          <Drawer
            anchor="right"
            open={openSubCategoryDrawer}
            onClose={() => setOpenSubCategoryDrawer(false)}
            sx={{ width: "50%" }}
          >
            <Box sx={{ width: 340, p: 2 }} role="presentation">
              <Typography variant="h6" sx={{ mb: 1 }}>
                Add Sub Category
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <TextField
                label="Sub Category Name"
                fullWidth
                value={newSubCategory}
                onChange={(e) => setNewSubCategory(e.target.value)}
              />

              <div className="mt-3">
                <Select
                  options={categoryOptions}
                  value={
                    newSubCategoryCat
                      ? {
                          value: newSubCategoryCat.id,
                          label: newSubCategoryCat.name,
                        }
                      : null
                  }
                  onChange={(option) => {
                    const cat =
                      categories.find((c) => c.id === option.value) || null;
                    setNewSubCategoryCat(cat);
                  }}
                  placeholder="Select Category"
                />
              </div>

              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Button variant="contained" onClick={addSubCategory}>
                  Save
                </Button>
                <Button onClick={() => setOpenSubCategoryDrawer(false)}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Drawer>
        </div>
      )}
    </>
  );
};

export default AddProductModal;
